import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE as API, cachedFetch } from "../../api/api";
import "./ProductMosaic.css";
import Logo from "../shared/Logo";
import { imageProps, SIZES } from "../../utils/imageUrl";

const COLS     = 4;
const ROWS     = 3;
const CELLS    = COLS * ROWS; // 12

// Real, eligible product images only — GET /products already applies the
// same is_active/stock/age-restriction filtering every other listing on
// the site gets (see backend/app/main.py's _is_age_verified_adult), so
// this purely decorative collage can never surface a restricted product
// to an ineligible viewer without any extra code here. Deduped by
// image_url so the same photo is never assigned to two cells at once.
const uniqueProductImages = (products) => {
  const seen = new Set();
  const urls = [];
  for (const p of products) {
    if (p?.image_url && !seen.has(p.image_url)) {
      seen.add(p.image_url);
      urls.push(p.image_url);
    }
  }
  return urls;
};

const shuffled = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// FLIP TIMING (must match CSS transition duration)
//
// The slow rotation, restored by the owner on 2026-10-13 after PR #62 had
// made it faster (0.7 s, two tiles every 8 s) to cut how often a dark back
// face shows (38 of 60 frames measured at this timing). The owner prefers
// this look; ProductMosaic.test.jsx pins these values.
export const FLIP_DURATION_MS = 1800; // how long one flip direction takes
export const PAUSE_ON_BACK_MS = 600;  // how long to linger on the back face
export const BATCH            = 3;       // cells to flip per wave
export const STAGGER_MS       = 800;     // gap between each cell in a wave
export const INTERVAL_MS      = 7_000;   // time between waves

const emptyCell = () => ({ src: null, flipped: false });

const ProductMosaic = () => {
  const [cells,      setCells]      = useState(() => Array.from({ length: CELLS }, emptyCell));
  const [poolReady,  setPoolReady]  = useState(false);
  const poolRef     = useRef([]);
  const timeoutsRef = useRef([]); // all pending timeouts — cleared on unmount

  // ── Pull images strictly from real, eligible products ───────────
  useEffect(() => {
    cachedFetch(`${API}/products?limit=60`)
      .then((products) => {
        const pool = uniqueProductImages(Array.isArray(products) ? products : []);
        poolRef.current = pool;
        if (pool.length) {
          // A shuffled, non-repeating fill when the pool is large enough
          // to cover every cell — guarantees no visible duplicate at
          // first paint, not left to independent random picks.
          const fill = shuffled(pool);
          setCells(Array.from({ length: CELLS }, (_, i) => ({
            src: fill[i % fill.length],
            flipped: false,
          })));
        }
        // pool.length === 0 → cells stay empty (mosaic-cell--empty); no
        // fallback imagery is substituted.
        setPoolReady(pool.length > 0);
      })
      .catch(() => { poolRef.current = []; setPoolReady(false); });
  }, []);

  // Picks an image not currently shown in any OTHER cell, so the visible
  // grid never has two cells showing the same product photo at once —
  // only falls back to a plain random pick if the pool is too small to
  // avoid it (fewer unique product images than cells).
  const pickUnusedImage = useCallback((excludeIdx, currentCells) => {
    const pool = poolRef.current;
    if (!pool.length) return null;
    const shown = new Set(currentCells.filter((_, i) => i !== excludeIdx).map((c) => c.src));
    const candidates = pool.filter((src) => !shown.has(src));
    const from = candidates.length ? candidates : pool;
    return from[Math.floor(Math.random() * from.length)];
  }, []);

  // ── Slow-motion flip sequence for a single cell ───────────────
  //   Phase 1 (0 ms)         : flip to back  [FLIP_DURATION_MS]
  //   Phase 2 (+FLIP_DURATION): swap src, linger on back face  [PAUSE_ON_BACK_MS]
  //   Phase 3 (+PAUSE)        : flip back to front  [FLIP_DURATION_MS]
  const changeCell = useCallback((idx) => {
    // Phase 1 — rotate to back
    setCells((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], flipped: true };
      return next;
    });

    // Phase 2 — swap image once back face is fully showing
    const t1 = setTimeout(() => {
      setCells((prev) => {
        const next = [...prev];
        next[idx] = { src: pickUnusedImage(idx, prev), flipped: true };
        return next;
      });

      // Phase 3 — rotate back to front after a brief pause
      const t2 = setTimeout(() => {
        setCells((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], flipped: false };
          return next;
        });
      }, PAUSE_ON_BACK_MS);
      timeoutsRef.current.push(t2);

    }, FLIP_DURATION_MS);
    timeoutsRef.current.push(t1);
  }, [pickUnusedImage]);

  // ── Batch animation: every ~7 s flip 3 cells, each 800 ms apart ─
  useEffect(() => {
    if (!poolReady) return;

    let queue = Array.from({ length: CELLS }, (_, i) => i)
      .sort(() => Math.random() - 0.5);

    const runWave = () => {
      if (queue.length < BATCH) {
        const fresh = Array.from({ length: CELLS }, (_, i) => i)
          .sort(() => Math.random() - 0.5);
        queue = [...queue, ...fresh];
      }

      const chosen = queue.splice(0, BATCH);

      chosen.forEach((cellIdx, i) => {
        const t = setTimeout(() => changeCell(cellIdx), i * STAGGER_MS);
        timeoutsRef.current.push(t);
      });
    };

    runWave();
    const iv = setInterval(runWave, INTERVAL_MS);

    return () => {
      clearInterval(iv);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [poolReady, changeCell]);

  return (
    <div className="editorial-mosaic" aria-hidden="true">
      <div className="mosaic-grid">
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`mosaic-cell${!cell.src ? " mosaic-cell--empty" : ""}${cell.flipped ? " mosaic-cell--flipped" : ""}`}
          >
            <div className="mosaic-flip-inner">
              <div className="mosaic-face">
                {cell.src && (
                  <img
                    {...imageProps(cell.src, "product", SIZES.mosaic)}
                    alt=""
                    className="mosaic-img"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="mosaic-back">
                <Logo className="mosaic-back-logo" alt="" />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Right-edge gradient so the grid blends into the editorial body */}
      <div className="mosaic-fade-r" />
    </div>
  );
};

export default ProductMosaic;
